/**
 * Discount code form for the cart drawer and the cart page.
 *
 * POST /cart/update.js {discount: CODE} both applies a code and reports back
 * whether Shopify accepted it, via cart.discount_codes[].applicable. There is
 * no dedicated AJAX endpoint for discount codes, and GET /discount/{CODE} gives
 * no way to tell a good code from a bad one, so this is the only route that can
 * validate inline.
 *
 * A rejected code is cleared straight away. Left in place it would sit on the
 * cart and stop a subsequent valid code from applying.
 *
 * The accepted code is also mirrored into the applied_discount_code cart
 * attribute, because Liquid's cart.discount_codes can read null server-side on
 * a cart that genuinely has a discount. snippets/cart-discount-form.liquid
 * reads that attribute for its server-rendered state.
 *
 * Listeners are delegated from document so they survive the cart-form section
 * re-render, which replaces this markup wholesale.
 */
(function () {
  const qs = (sel, scope) => (scope || document).querySelector(sel);
  const qsa = (sel, scope) => Array.from((scope || document).querySelectorAll(sel));
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function setDiscount(code) {
    return fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: code,
        attributes: { applied_discount_code: code },
      }),
    }).then((r) => r.json());
  }

  function setMessage(scope, text, isError) {
    const el = qs('.js-code-msg', scope);
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-error', !!isError);
    el.classList.toggle('is-success', !!text && !isError);
    el.hidden = !text;
  }

  function renderApplied(scope, code) {
    const applied = qs('.js-code-applied', scope);
    const tag = qs('.js-code-tag', scope);
    const input = qs('.js-code-input', scope);

    if (tag) tag.textContent = code || '';
    if (applied) applied.hidden = !code;
    if (input && code) input.value = '';

    // A live-applied code leaves the panel open, matching the server-rendered
    // state on a fresh page load.
    if (code) {
      const body = qs('.cart-code__body', scope);
      const toggle = qs('.js-code-toggle', scope);
      if (body) body.hidden = false;
      scope.classList.add('cart-code--open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
  }

  /** Re-sync every discount form on the page, then let the cart surfaces redraw. */
  function afterChange() {
    return fetch(`${root()}cart.js`)
      .then((r) => r.json())
      .then((cart) => {
        const active = (cart.discount_codes || []).filter((d) => d.applicable)[0];
        qsa('.cart-code').forEach((scope) => renderApplied(scope, active ? active.code : ''));
        // cart-form listens for this and re-fetches its section, which redraws
        // the line items, the discount summary and the subtotal.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        return cart;
      });
  }

  function applyCode(scope, btn) {
    const input = qs('.js-code-input', scope);
    const code = input ? input.value.trim() : '';
    if (!code) {
      setMessage(scope, 'Enter a discount code.', true);
      return;
    }

    btn.disabled = true;
    setMessage(scope, '');

    setDiscount(code)
      .then((cart) => {
        const entry = (cart.discount_codes || []).filter(
          (d) => d.code.toLowerCase() === code.toLowerCase()
        )[0];

        if (entry && entry.applicable) {
          setMessage(scope, 'Discount applied.', false);
          return afterChange();
        }

        return setDiscount('').then(() => {
          setMessage(scope, "That discount code isn't valid for this cart.", true);
        });
      })
      .catch(() => setMessage(scope, 'Something went wrong. Please try again.', true))
      .finally(() => {
        btn.disabled = false;
      });
  }

  function removeCode(scope, btn) {
    btn.disabled = true;
    setDiscount('')
      .then(() => {
        setMessage(scope, '');
        return afterChange();
      })
      .catch(() => setMessage(scope, 'Something went wrong. Please try again.', true))
      .finally(() => {
        btn.disabled = false;
      });
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.js-code-toggle');
    if (toggle) {
      const wrap = toggle.closest('.cart-code');
      const body = qs('.cart-code__body', wrap);
      const willOpen = body.hidden;
      body.hidden = !willOpen;
      wrap.classList.toggle('cart-code--open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        const inp = qs('.js-code-input', wrap);
        if (inp) inp.focus();
      }
      return;
    }

    const applyBtn = e.target.closest('.js-code-apply');
    if (applyBtn) {
      applyCode(applyBtn.closest('.cart-code'), applyBtn);
      return;
    }

    const removeBtn = e.target.closest('.js-code-remove');
    if (removeBtn) {
      removeCode(removeBtn.closest('.cart-code'), removeBtn);
    }
  });

  // Enter inside the field submits, without submitting any surrounding form.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('.js-code-input');
    if (!input) return;
    e.preventDefault();
    const applyBtn = qs('.js-code-apply', input.closest('.cart-code'));
    if (applyBtn) applyBtn.click();
  });
})();
